import { yupResolver } from "@hookform/resolvers/yup";
import {
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Button, DeleteIcon, Input, PlusIcon } from "components";
import Decimal from "decimal.js";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import {
  Control,
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import * as yup from "yup";

const schema = yup.object().shape({
  tax_name: yup.string().required("Name is required"),
  compound_tax: yup
    .array()
    .of(
      yup.object().shape({
        tax_name: yup.string().required("Name is required"),
        tax_rate: yup
          .number()
          .transform((value, originalValue) =>
            String(originalValue).trim() === "" ? undefined : value,
          )
          .required("tax rate is required")
          .test("percentage", "tax rate must be greater than 0", (value) => {
            return value && value > 0 ? true : false;
          }),
        is_percentage: yup.boolean().default(true),
        is_custom: yup.boolean().default(false),
      }),
    )
    .min(1, "At least one tax rate is required")
    .typeError("At least one tax rate is required")
    .max(1000, "Tax rate must be less than 1000"),
});

interface SingleTaxTabProps {
  mode?: {
    method: "create" | "update";
    data: any;
    primaryButtonDisable: boolean;
  };
}
interface CompoundTaxItem {
  tax_name: string;
  tax_rate: number;
  is_percentage: boolean;
  is_custom: boolean;
  mapping_id?: string;
}

interface FormValues {
  tax_name: string;
  compound_tax: CompoundTaxItem[];
}

export const CompoundTaxTab = forwardRef((props: SingleTaxTabProps, ref) => {
  const { mode } = props;
  const theme = useTheme();
  // Prepare default values based on mode
  const defaultValues = useMemo(() => {
    if (mode?.method === "update" && mode.data) {
      return {
        tax_name: mode.data.tax_name,
        compound_tax: mode.data.compound_tax.map((tax: any) => ({
          tax_name: tax.tax_name,
          tax_rate: tax.tax_rate,
          is_percentage: tax.is_percentage,
          is_custom: tax.is_custom,
          // Include mapping_id if you need it for updates
          mapping_id: tax.mapping_id,
        })),
      };
    }
    return {
      tax_name: "",
      compound_tax: [
        {
          tax_name: "",
          tax_rate: 0,
          is_percentage: true,
          is_custom: false,
        },
      ],
    };
  }, [mode]);
  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    // defaultValues: {
    //   tax_name: "",
    //   compound_tax: [],
    // },
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = form;

  useEffect(() => {
    if (mode?.method === "update" && mode.data) {
      reset(defaultValues);
    }
  }, [mode?.data, reset, defaultValues]);

  const { fields, append, prepend, remove, swap, move, insert } =
    useFieldArray<any>({
      control,
      name: "compound_tax",
    });

  const onAddNewTaxHandler = () => {
    append({
      tax_name: "",
      tax_rate: 0,
      is_percentage: true,
      is_custom: false,
    });
  };

  const onDeleteTaxHandler = (index: number) => {
    remove(index);
  };

  const onSubmitHandler = async () => {
    let newData;
    await handleSubmit((data) => {
      newData = {
        ...data,
        tax_rate: new Decimal(
          data.compound_tax.reduce((acc: number, curr: any) => {
            return new Decimal(acc)
              .add(new Decimal(curr.tax_rate))
              .toDecimalPlaces(3)
              .toNumber();
          }, 0),
        )
          .toDecimalPlaces(3)
          .toNumber(),
      };
    })();
    return newData;
  };

  useImperativeHandle(ref, () => ({
    onSubmit: onSubmitHandler,
  }));

  return (
    <FormProvider {...form}>
      <Stack
        sx={{
          gap: "16px",
        }}
      >
        <Typography variant="subtitle1">Combined tax</Typography>
        <Input
          name="tax_name"
          placeholder="Eg. Sales tax"
          control={control}
          error={!!errors.tax_name}
          errorMessage={errors.tax_name?.message as string | undefined}
          inputProps={{
            maxLength: 50,
          }}
        />

        <Stack
          gap="16px"
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack>
            <Typography variant="subtitle1">Combined tax rates</Typography>
            <Typography variant="body-small" color={theme.palette.lightBg.low}>
              Add individual tax rates that make up this combined tax.
            </Typography>
            {errors.compound_tax && (
              <Typography variant="body-small" color={theme.palette.error.main}>
                {errors.compound_tax.message as string}
              </Typography>
            )}
          </Stack>
          <Button
            disabled={fields.length >= 7}
            variant="secondary"
            endIcon={<PlusIcon />}
            onClick={onAddNewTaxHandler}
            sx={{
              width: "fit-content",
              height: "fit-content",
            }}
          >
            Add tax
          </Button>
        </Stack>
        <Stack gap="16px">
          {fields.map((field, index) => (
            <TaxItem key={field.id} index={index} remove={onDeleteTaxHandler} />
          ))}
        </Stack>
      </Stack>
      <TotalTax />
    </FormProvider>
  );
});

const TaxItem = ({
  index,
  remove,
}: {
  index: number;
  remove: (index: number) => void;
}) => {
  const theme = useTheme();

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Stack
      sx={{
        gap: "16px",
        border: "1px solid",
        borderColor: theme.palette.neutral["020"],
        borderRadius: "8px",
        p: "16px",
      }}
    >
      {/* <input
        type="hidden"
        {...register(`compound_tax.${index}.mapping_id` as const)}
      /> */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="subtitle1"
          sx={{
            padding: "4px 8px",
            bgcolor: "#CDCAFF",
            color: "#311679",
            borderRadius: "4px",
          }}
        >
          Tax rate {index + 1}
        </Typography>
        <IconButton onClick={() => remove(index)}>
          <DeleteIcon htmlColor={theme.palette.lightBg.high} />
        </IconButton>
      </Stack>
      <Input
        name={`compound_tax.${index}.tax_name`}
        label="Tax name"
        control={control}
        error={!!(errors?.compound_tax as any)?.[index]?.tax_name}
        errorMessage={
          ((errors?.compound_tax as any)?.[index]?.tax_name
            ?.message as string) || undefined
        }
        inputProps={{
          maxLength: 50,
        }}
        // {...register(`compound_tax.${index}.tax_name` as const)}
      />
      <Controller
        control={control}
        name={`compound_tax.${index}.tax_rate`}
        render={({ field }) => (
          <Input
            {...field}
            value={
              field.value
                ? new Decimal(field.value).div(100).toNumber()
                : field.value
            }
            digitsAfterDecimal={3}
            onBlur={(e) => {
              if (
                e.target.value &&
                new Decimal(e.target.value).toNumber() > 100
              ) {
                field.onChange(10000);
              }
            }}
            onChange={(e) => {
              if (
                e.target.value &&
                new Decimal(e.target.value).toNumber() > 100
              ) {
                e.target.value = "100";
              }
              field.onChange(
                e.target.value
                  ? new Decimal(e.target.value).mul(100).toNumber()
                  : e.target.value,
              );
            }}
            label="Rate (%)"
            error={!!(errors?.compound_tax as any)?.[index]?.tax_rate}
            errorMessage={
              ((errors?.compound_tax as any)?.[index]?.tax_rate
                ?.message as string) || undefined
            }
            type="number"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              step: 0.1,
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "E" ||
                e.key === "+" ||
                e.key === "-"
              ) {
                e.preventDefault();
              }
            }}
            // {...register(`compound_tax.${index}.tax_rate` as const)}
          />
        )}
      />
    </Stack>
  );
};

const TotalTax = () => {
  const theme = useTheme();
  const { watch, control } = useFormContext();

  const compoundTaxData = watch("compound_tax") || [];

  const { totalTax, taxNames } = useMemo(() => {
    const totalTax = compoundTaxData.reduce((acc: number, curr: any) => {
      try {
        return new Decimal(
          curr?.tax_rate ? new Decimal(curr.tax_rate).div(100).toNumber() : 0,
        )
          .add(new Decimal(acc))
          .toDecimalPlaces(3)
          .toNumber();
      } catch (error) {
        return 0;
      }
    }, 0);

    const taxNames = compoundTaxData
      .map((item: any) => item.tax_name)
      .join(" + ");

    return { totalTax, taxNames };
  }, [
    ...compoundTaxData.map((item: any) => item.tax_rate),
    ...compoundTaxData.map((item: any) => item.tax_name),
  ]);

  return (
    <>
      {compoundTaxData.length > 0 && (
        <Stack
          direction="row"
          sx={{
            width: "100%",
            position: "absolute",
            bottom: "0px",
            left: "0px",
            right: "0px",
            p: "16px",
            borderRadius: "8px 8px 0px 0px",
            boxShadow: "0px -4px 16px 0px rgba(0, 0, 0, 0.08)",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            bgcolor: theme.palette.darkBg.main,
            zIndex: 1,
          }}
        >
          <Typography variant="body-medium" sx={{ flex: 1 }}>
            Total tax rate:{" "}
            {taxNames.length > 0 && (
              <Typography
                component="span"
                variant="body-small"
                color={theme.palette.lightBg.low}
              >
                ({taxNames})
              </Typography>
            )}
          </Typography>
          <Typography
            variant="body-small"
            color={theme.palette.lightBg.low}
            sx={{}}
          >
            {totalTax}%
          </Typography>
        </Stack>
      )}
    </>
  );
};
